import { Box, Button, Grid, TextField } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import {
  CarrierType,
  ContainerDamageResponseDto,
  OrderResponseDto,
  UnitType,
} from '@planning/app/api'
import { IContainerDamage } from '@planning/components'
import { ControlledContainerSealsWithTypes } from '@planning/components/ControlledContainerSealsWithTypes'
import { useUnitTypeLabel } from '@planning/components/UnitType'
import { ControlledDamageRecordBox } from '@planning/components/damage-report/ControlledDamageRecordBox'
import { OogSubForm } from '@planning/components/organisms/OogSubForm/OogSubForm'
import {
  createOogFormDataFromOrderResponse,
  OogFormData,
} from '@planning/components/organisms/OogSubForm/oog-helper'
import { UnitLabelSubForm } from '@planning/components/organisms/UnitLabelSubForm/UnitLabelSubForm'
import { NavigationStackBackButton } from '@planning/pages/TallymanV2/Components/HeaderComponents/NavigationStackBackButton'
import { validateIsoCode } from '@planning/rt-stores/helpers'
import { useTranslate } from '@tolgee/react'
import {
  ContainerMobile,
  CustomInputAdornment,
  Header,
  StepperInfo,
  useMinimalsTheme,
} from '@tom-ui/ui'
import { notAllowPlusMinusPointSign, onlyAllowPositiveInt, useFormStore } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'
import { Controller, FieldErrors, useForm } from 'react-hook-form'

interface IProps {
  order: OrderResponseDto
  damages?: IContainerDamage[]
  submitButtonText: string
  displayDamageReport?: boolean
  onSubmit: (data: IInspectContainerFormData) => Promise<void> | void
}

export interface IInspectContainerFormData extends OrderResponseDto {
  carrierType: CarrierType
  damages: ContainerDamageResponseDto[]
  damagesReported?: IContainerDamage[]
  hasDamage?: boolean
  oog?: OogFormData
  isEdited?: boolean
  isChecked?: boolean
  journeyId?: number
}

export const InspectContainer: FC<IProps> = observer(
  ({ order, damages, submitButtonText, displayDamageReport, onSubmit }) => {
    const { t } = useTranslate()
    const { containerItemStore, appViewStore } = usePlanningStore()
    const theme = useMinimalsTheme()

    const formStore = useFormStore<IInspectContainerFormData>()

    useEffect(() => {
      if (order.containerId) containerItemStore.fetchById(order.containerId)
    }, [containerItemStore, order.containerId])

    const containerItem = computed(() => containerItemStore.elements[order.containerId ?? 0]).get()

    // TODO: Extract form as component without header
    const {
      register,
      control,
      handleSubmit,
      getValues,
      setValue,
      watch,
      formState: { errors },
    } = useForm({
      defaultValues: {
        id: order?.id,
        referenceNumber: order?.referenceNumber,
        direction: order?.direction,
        carrierType: order.carrierVisitType ?? CarrierType.Truck,
        containerId: order.containerId,
        containerNumber: order?.containerNumber,
        containerIsoCode: order?.containerIsoCode,
        containerTare: order?.containerTare,
        containerMaxGross: order?.containerMaxGross,
        grossWeight: order?.grossWeight,
        containerType: order?.containerType,
        containerLength: order?.containerLength,
        containerHeight: order?.containerHeight,
        content: order?.content,
        consignee: order?.consignee,
        isoCodeArchetype: order?.isoCodeArchetype,
        notes: order?.notes,
        isEmpty: order?.isEmpty,
        imoClasses: order?.imoClasses,
        holds: order?.holds,
        damages: damages ?? containerItem?.data.damages ?? [],
        hasDamage: !!(damages?.length ?? containerItem?.activeDamages.length),
        damagesReported: damages ?? [],
        seals: order?.seals ?? [],
        hasSeals: order?.hasSeals ?? false,
        isJobFinished: order?.isJobFinished,
        isOnTerminal: order?.isOnTerminal,
        oog: (order as IInspectContainerFormData)?.oog ?? createOogFormDataFromOrderResponse(order),
        unitLabel: order?.unitLabel,
        isEdited: (order as IInspectContainerFormData)?.isEdited,
        plannedYardLocation: order?.plannedYardLocation,
        journeyId: (order as IInspectContainerFormData)?.journeyId,
      } as IInspectContainerFormData,
    })

    useEffect(() => {
      if (containerItem) {
        setValue('hasDamage', !!(damages?.length ?? containerItem?.data.damages.length))
      }
    }, [containerItem, damages?.length, setValue])

    const [defaultValuesData] = useState(getValues())

    const pageTitle = `Inspect ${useUnitTypeLabel(containerItem && containerItem?.data ? containerItem?.data?.unitType : UnitType.Container)}`
    const unitTypeIsContainer = containerItem?.data.unitType === UnitType.Container

    const onFormSubmit = async (data: IInspectContainerFormData) => {
      try {
        data.isEdited = data.isEdited
          ? true
          : JSON.stringify(defaultValuesData) !== JSON.stringify(data)
        data.isChecked = true
        onSubmit(data)
      } catch (error) {
        appViewStore.setShowAlert('error', t('failedToSave', 'Failed to save'))
      }
    }

    const onEditDamageReport = (
      editedReport: Partial<ContainerDamageResponseDto>,
      initialReport: ContainerDamageResponseDto,
    ): void => {
      setValue('damages', [
        ...getValues('damages').filter(d => d.description !== initialReport.description),
        editedReport as ContainerDamageResponseDto,
      ])
    }

    const containerIsoCodeErrorText = (errors: FieldErrors) => {
      const isoCodeError = errors.containerIsoCode
      if (isoCodeError) {
        const errorType = isoCodeError.type
        if (errorType === 'required') return t('fieldIsRequired', 'Field is required.')
        if (errorType === 'validate') return t('isoCodeNotFound', 'ISO Code not found')

        return isoCodeError.message?.toString()
      }
    }

    const getStepText = () => {
      return watch('hasDamage') ? t('1of2', '1 of 2') : t('1of1', '1 of 1')
    }

    const getButtonText = () => {
      return watch('hasDamage') ? t('next', 'Next') : submitButtonText
    }

    return (
      <>
        <Header
          leftRenderOption={<NavigationStackBackButton />}
          title={order?.containerNumber ?? ''}
          rightRenderOption={
            <Button
              variant='contained'
              onClick={handleSubmit(onFormSubmit)}
              sx={{ width: '15%', minWidth: '110px' }}
              data-cy='gate-in-inspect-container-submit-btn'
            >
              {getButtonText()}
            </Button>
          }
        />
        <StepperInfo title={pageTitle} steps={getStepText()} />
        {order && (
          <form>
            <ContainerMobile sx={{ pt: theme.customSpacing.l }}>
              {unitTypeIsContainer && (
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label={`${t('iso', 'ISO')}`}
                    required
                    variant='outlined'
                    {...register('containerIsoCode', {
                      required: true,
                      validate: async v => !!v && (await validateIsoCode(v)),
                    })}
                    name='containerIsoCode'
                    sx={{ marginTop: '1rem' }}
                    error={!!errors.containerIsoCode}
                    helperText={containerIsoCodeErrorText(errors)}
                  />
                </Grid>
              )}
              <Grid size={12}>
                <Controller
                  control={control}
                  name={`isEmpty`}
                  render={({ field: { value } }) => (
                    <TextField
                      fullWidth
                      label={`${t('state', 'State')}`}
                      variant='outlined'
                      value={value ? t('empty', 'Empty') : t('full', 'Full')}
                      name='isEmpty'
                      sx={{ marginTop: '1rem' }}
                      disabled
                    />
                  )}
                />
                <Grid size={12}>
                  <TextField
                    fullWidth
                    required
                    label={`${t('maxGrossWeight', 'Max. gross weight')}`}
                    variant='outlined'
                    type='number'
                    InputProps={{
                      endAdornment: (
                        <CustomInputAdornment position='end' color='secondary'>
                          kg
                        </CustomInputAdornment>
                      ),
                    }}
                    {...register('containerMaxGross', {
                      valueAsNumber: true,
                      required: {
                        value: true,
                        message: t(
                          'pleaseEnterANumberGreaterThan0',
                          'Please enter a number greater than 0.',
                        ),
                      },
                      max: {
                        value: 99999,
                        message: t(
                          'pleaseEnterANumberLessThan99999',
                          'Please enter a number less than 99999.',
                        ),
                      },
                    })}
                    onKeyDown={notAllowPlusMinusPointSign}
                    onChange={onlyAllowPositiveInt}
                    name='containerMaxGross'
                    error={!!errors.containerMaxGross}
                    helperText={errors.containerMaxGross?.message}
                    sx={{ marginTop: '1rem' }}
                    data-cy='gate-in-inspect-container-max-gross-field'
                  />
                </Grid>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  required
                  label={`${t('tareWeight', 'Tare weight')}`}
                  variant='outlined'
                  type='number'
                  InputProps={{
                    endAdornment: (
                      <CustomInputAdornment position='end' color='secondary'>
                        kg
                      </CustomInputAdornment>
                    ),
                  }}
                  {...register('containerTare', {
                    valueAsNumber: true,
                    required: {
                      value: true,
                      message: t(
                        'pleaseEnterANumberGreaterThan0',
                        'Please enter a number greater than 0.',
                      ),
                    },
                    max: {
                      value: 99999,
                      message: t(
                        'pleaseEnterANumberLessThan99999',
                        'Please enter a number less than 99999.',
                      ),
                    },
                  })}
                  onKeyDown={notAllowPlusMinusPointSign}
                  onChange={onlyAllowPositiveInt}
                  name='containerTare'
                  error={!!errors.containerTare}
                  helperText={errors.containerTare?.message}
                  sx={{ marginTop: '1rem' }}
                  data-cy='gate-in-inspect-container-tare-weight-field'
                />
              </Grid>
              <Grid sx={{ marginTop: '1rem' }} size={12}>
                <Controller
                  control={control}
                  name={`unitLabel`}
                  render={({ field: { onChange, value } }) => (
                    <UnitLabelSubForm
                      formStore={formStore}
                      name='unitLabel'
                      initialValue={value}
                      onChange={unitLabel => onChange(unitLabel)}
                    />
                  )}
                />
              </Grid>
              {displayDamageReport && containerItem && (
                <Grid size={12}>
                  <Box sx={{ marginTop: '1rem !important' }}>
                    <ControlledDamageRecordBox control={control} name='hasDamage' />
                  </Box>
                </Grid>
              )}

              {!getValues('isEmpty') && (
                <Grid sx={{ marginTop: '1rem' }} size={12}>
                  <Controller
                    control={control}
                    name={'oog'}
                    render={({ field: { onChange, value } }) => (
                      <OogSubForm
                        formStore={formStore}
                        name='oog'
                        initialValue={value}
                        onChange={oog => onChange(oog)}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid sx={{ marginTop: '1rem' }} size={12}>
                <ControlledContainerSealsWithTypes
                  hasSeals={getValues('hasSeals') ?? false}
                  seals={getValues('seals') ?? []}
                  onChangeHasSeals={updatedHasSeals => {
                    setValue(`hasSeals`, updatedHasSeals)
                  }}
                  onChangeSeals={updatedSeals => {
                    setValue(`seals`, updatedSeals)
                  }}
                />
              </Grid>
            </ContainerMobile>
          </form>
        )}
      </>
    )
  },
)
